import React from 'react';

interface PoeticButtonProps {
    onClick?: (e: React.MouseEvent) => void;
    children: React.ReactNode;
    className?: string;
    showIcon?: boolean;
    variant?: 'pill' | 'circle';
    'aria-label'?: string;
    disabled?: boolean;
}

const PoeticButton: React.FC<PoeticButtonProps> = ({
    onClick,
    children,
    className = "",
    showIcon = true,
    variant = 'pill',
    disabled = false,
    ...rest
}) => {
    const isPill = variant === 'pill';

    return (
        <button
            type="button"
            onClick={disabled ? undefined : onClick}
            disabled={disabled}
            className={`group relative ${isPill ? 'px-10 py-4 min-h-[48px] rounded-2xl' : 'w-14 h-14 min-w-[48px] min-h-[48px] rounded-full'} bg-black/60 backdrop-blur-xl border border-cyan-500/30 overflow-hidden transition-all duration-300 ease-out ${disabled ? 'opacity-30 cursor-not-allowed grayscale' : 'hover:border-cyan-400/60 hover:bg-cyan-500/15 hover:shadow-[0_0_40px_rgba(34,211,238,0.25)] active:scale-95'} focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-2 focus-visible:ring-offset-black flex items-center justify-center touch-manipulation ${className} will-change-transform`}
            {...rest}
        >
            {/* Outer glow ring */}
            <div className={`absolute -inset-0.5 bg-gradient-to-r from-cyan-500/0 via-cyan-400/0 to-cyan-500/0 ${isPill ? 'rounded-2xl' : 'rounded-full'} opacity-0 group-hover:opacity-100 group-hover:from-cyan-500/20 group-hover:via-cyan-400/10 group-hover:to-cyan-500/20 blur-md transition-all duration-500`} />

            {/* Inner shine lines */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-400/60 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-cyan-400/30 to-transparent" />
                {isPill && (
                    <>
                        <div className="absolute inset-y-0 left-0 w-px bg-gradient-to-b from-transparent via-cyan-400/30 to-transparent" />
                        <div className="absolute inset-y-0 right-0 w-px bg-gradient-to-b from-transparent via-cyan-400/30 to-transparent" />
                    </>
                )}
            </div>

            {/* Breathing pulse background */}
            <div className={`absolute inset-0 bg-cyan-400/0 group-hover:bg-cyan-400/5 ${isPill ? 'rounded-2xl' : 'rounded-full'} transition-all duration-700`} />

            {/* Content */}
            <span className={`relative z-10 flex items-center justify-center ${isPill ? 'gap-3 tracking-[0.15em] uppercase text-sm font-medium' : ''} text-white/90 group-hover:text-white transition-colors duration-300`}>
                {children}
                {isPill && showIcon && (
                    <svg
                        className="w-5 h-5 text-cyan-400 group-hover:translate-x-1 group-hover:text-cyan-300 transition-all duration-300"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                )}
            </span>

            {/* Periodic gleam effect - subtle shine animation */}
            <div className="absolute inset-0 opacity-20 pointer-events-none overflow-hidden">
                <div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/50 to-transparent -skew-x-12 animate-gleam"
                />
            </div>


        </button>
    );
};

export default React.memo(PoeticButton);
